/*
 * Ported from the Next app, then from the Astro storefront, and unchanged by
 * both moves except for its shell.
 *
 * It has always been React — several hundred lines of laid-out copy using the
 * brand components — which is why neither move touched the words. What changed
 * the second time is where the shelf comes from: Astro awaited the catalogue in
 * frontmatter and handed it down, and the page above now fetches it, because a
 * React component cannot be async.
 */
import { Rule, ui } from '../ui';
import { brand, Cta, Do, Eyebrow, Never, PanelBar, Runner, Stat } from '../ui';
import { CapsuleGrid, type CapsuleTitle } from '../ui';
import { Reveal } from '../ui';

/**
 * The creator pitch. Paper, Ink and Hot, and nothing else.
 *
 * **A storefront, because that is what a creator is here to do.** The page used
 * to be four blocks of prose about the programme with a link at the bottom, and
 * the thing a creator actually wants — what is on the shelf and what it pays —
 * was three clicks away behind a sign-in. So the games are on the page, with a
 * commission where a shopper would see a price.
 *
 * **They are our games, and the page says so now.** It used to talk about *the
 * games you already play* and *each studio's rate*, which described the open
 * marketplace this has not been since #140. There is one catalogue, we made all
 * of it, and one rate floor applies across it — a creator deciding whether to
 * spend a video on us is owed that in the first sentence rather than after they
 * have joined.
 *
 * **The figures are floors, and say so.** The opportunity board is gated to
 * approved creators because a title's real rate is commercially sensitive
 * (section 3.7), and nothing here leaks one: every number is that title's
 * public price run through the real split engine at the published 5% minimum.
 * So the shelf understates on purpose — a creator who joins finds the board
 * pays more than the shop window promised, which is the right direction for a
 * number to be wrong in.
 *
 * **Short words, and about half the prose.** Every paragraph used to restate
 * its own heading before saying anything new, and a creator scanning for the
 * rate had to read past all of it. The figures, the notice period and the
 * clearing period are unchanged, because those are the parts somebody is
 * deciding on.
 */
export function CreatorsPitch({ shelf }: { shelf: CapsuleTitle[] }): React.ReactElement {
  /**
   * The way in, and it is a relative path again.
   *
   * It was absolute for two deployments' worth of history: `/onboarding` on the
   * storefront's origin was a 404, so the button on the page whose entire job
   * is recruiting creators landed on the not-found page, and the fix was to
   * name the app's origin explicitly. One application serves both now and a
   * path is all this needs.
   *
   * **`/onboarding/creator` and not `/onboarding`**, which survives the merge
   * unchanged and is the half that still matters. The bare path answers from
   * the hostname, and on `app.goodbuild.cc` and on the apex that answer is a
   * *studio* registration — so a creator pressing this would have been handed a
   * form asking for a company slug.
   */
  const applyHref = '/onboarding/creator';
  const signUpHref = '/sign-up';

  return (
    <>
      <Runner left="Creators" />

      <header style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gb-space-6)' }}>
        <Eyebrow>For creators</Eyebrow>
        <h1 className="gb-display" style={{ color: 'var(--gb-accent)' }}>
          Show our
          <br />
          games.
          <br />
          Get paid.
        </h1>
        <p className="gb-lead">
          Play our games on your channel. Earn money every time somebody buys because of you, and
          see exactly which sales those were.
        </p>
        <div style={{ display: 'flex', gap: 'var(--gb-space-3)', flexWrap: 'wrap' }}>
          <Cta href={applyHref}>Apply now</Cta>
          <Cta href={signUpHref} variant="ghost">
            Create account
          </Cta>
        </div>
      </header>

      <PanelBar />

      {/*
        The shop window. Renders nothing when the catalogue is empty rather than
        drawing placeholder capsules — the same rule the store runs on, and it
        matters more here, because a fake shelf is a fake earning.
      */}
      {shelf.length > 0 && (
        <Reveal as="section" className={ui.stack}>
          <Eyebrow>Out right now</Eyebrow>
          <h2 className="gb-headline">
            What you
            <br />
            would earn
          </h2>
          <p className="gb-body" style={{ color: 'var(--gb-text-muted)' }}>
            Each figure is the least you would get from one sale, after tax and fees. Most games pay
            more, and the board shows you the real rate once you are in.
          </p>
          <CapsuleGrid titles={shelf} />
        </Reveal>
      )}

      <Reveal as="section" className={ui.stack}>
        <Eyebrow>The deal, up front</Eyebrow>
        <div className={brand.g4}>
          <Stat value="5%" label="The least a game can pay" />
          <Stat value="14" label="Days warning before a cut" />
          <Stat value="30" label="Days before you are paid" />
          <Stat value="1" label="One rate per game, in the open" />
        </div>
        <p className="gb-body" style={{ color: 'var(--gb-text-muted)' }}>
          One rate per game, on the board for everyone to see. A rise happens at once. A cut is
          blocked unless we told you fourteen days before.
        </p>
      </Reveal>

      <Rule />

      {/*
        The one wiped block on the page. Everything in it is type on rules, and
        none of it takes focus — which is what the variant needs, because the
        clip that uncovers a block is also a clip a focus ring cannot escape.
        The shelf above stays on `rise` for the other reason: a capsule's halo
        draws outside the card. See `Reveal`.
      */}
      <Reveal as="section" variant="wipe" className={ui.stack}>
        <h2 className="gb-headline">
          Pick what
          <br />
          to show
        </h2>

        <div className={brand.g2}>
          <div className={ui.point}>
            <h3 className="gb-subhead">See the money first</h3>
            <p className={`gb-body ${ui.pointBody}`}>
              Every game shows what you make per sale, and how many creators are already on it.
            </p>
          </div>
          <div className={ui.point}>
            <h3 className="gb-subhead">Every payment is listed</h3>
            <p className={`gb-body ${ui.pointBody}`}>
              Each one lists every order, and every refund on its own line. Your total never drops
              without saying why.
            </p>
          </div>
          <div className={ui.point}>
            <h3 className="gb-subhead">We find your videos</h3>
            <p className={`gb-body ${ui.pointBody}`}>
              Streams and videos are matched for you. Pasting a link works too, and counts straight
              away.
            </p>
          </div>
          <div className={ui.point}>
            <h3 className="gb-subhead">Keys and early copies</h3>
            <p className={`gb-body ${ui.pointBody}`}>
              Ask for keys, and play our games before they come out. Every code here is ours. We
              never touch other shops&rsquo; keys.
            </p>
          </div>
        </div>
      </Reveal>

      <Rule />

      <Reveal as="section" className={ui.stack}>
        <Eyebrow>What we ask</Eyebrow>
        <div className={brand.g2}>
          <Do title="You must">
            <ul className="gb-body" style={{ margin: 0 }}>
              <li>A review of a free copy gets a label. We add it, not you.</li>
              <li>Playing time next to your post is playing time you really had.</li>
            </ul>
          </Do>
          <Never title="Never">
            <ul className="gb-body" style={{ margin: 0 }}>
              <li>You never need a social account to buy a game. That is for features only.</li>
              <li>You never have to be nice about a game. Nothing here depends on that.</li>
            </ul>
          </Never>
        </div>
        <p className="gb-body" style={{ color: 'var(--gb-text-muted)' }}>
          The label is what keeps every other review on the page worth reading, including yours.
        </p>
      </Reveal>

      <div style={{ display: 'flex', gap: 'var(--gb-space-3)', flexWrap: 'wrap' }}>
        <Cta href={applyHref}>Apply now</Cta>
        <Cta href="/contact" variant="ghost">
          Ask us anything
        </Cta>
      </div>
    </>
  );
}
